/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import ReactPlayer from 'react-player'
import {
    Box,
    Text,
    Flex,
    Heading,
    Spacer,
    Button
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import useSegment from '../../../integrations/segment/app/hooks/use-segment'

/*
 * Video with text Banner Component.
 */

export const VideoWithTextBanner = ({
    videoUrl,
    custom,
    imageAltText,
    imageLink,
    backgroundColor,
    textColor,
    heading,
    subHeading,
    buttonText,
    buttonUrl
}) => {
    const [isClient, setIsClient] = useState(false)
    const segment = useSegment()

    useEffect(() => {
        setIsClient(true)
    }, [])

    return (
        <>
            <Flex
                pt={'40px'}
                height={{base: '250px', lg: '400px'}}
                direction={{base: 'column', lg: 'row'}}
            >
                <Box flex={{base: '100%', lg: '42%'}}>
                    {isClient && (
                        <ReactPlayer
                            url={videoUrl}
                            loop={true}
                            muted={true}
                            controls={true}
                            width="100%"
                            height="100%"
                        />
                    )}
                </Box>
                <Spacer />
                <HideOnMobile>
                    <Box flex={{base: '100%', lg: '57%'}}>
                        {custom?.image?.disBaseLink || backgroundColor ? (
                            custom?.image?.disBaseLink ? (
                                <Box>
                                    <a
                                        href={imageLink}
                                        onClick={(e) => {
                                            // segment
                                            segment.sendPanelSelectedEvent(e, {})
                                        }}
                                    >
                                        <img src={custom?.image?.disBaseLink} alt={imageAltText} />
                                    </a>
                                </Box>
                            ) : (
                                <Box bg={backgroundColor} height="100%" pos="relative">
                                    <Box
                                        color={textColor}
                                        w="250px"
                                        pos="absolute"
                                        top="50%"
                                        left="8%"
                                        zIndex={1}
                                        transform={'translateY(-50%)'}
                                    >
                                        <Heading as="h2" size="xl">
                                            {heading}
                                        </Heading>
                                        <Text>{subHeading}</Text>
                                        {buttonText && (
                                            <Button
                                                href={buttonUrl}
                                                onClick={(e) => {
                                                    // segment
                                                    segment.sendPanelSelectedEvent(e, {})
                                                }}
                                                size="lg"
                                                bg="rgba(255, 255, 255, 0.1)"
                                                mt={4}
                                                border="1px"
                                                borderColor={textColor}
                                            >
                                                {buttonText}
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            )
                        ) : (
                            <></>
                        )}
                    </Box>
                </HideOnMobile>
            </Flex>
            <HideOnDesktop>
                <Box flex={{base: '100%', lg: '57%'}} paddingTop={{base: '25px'}}>
                    {custom?.image?.disBaseLink || backgroundColor ? (
                        custom?.image?.disBaseLink ? (
                            <Box>
                                <a
                                    href={imageLink}
                                    onClick={(e) => {
                                        // segment
                                        segment.sendPanelSelectedEvent(e, {})
                                    }}
                                >
                                    <img src={custom?.image?.disBaseLink} alt={imageAltText} />
                                </a>
                            </Box>
                        ) : (
                            <Box bg={backgroundColor} height="100%" pos="relative">
                                <Box
                                    color={textColor}
                                    w="250px"
                                    pos="absolute"
                                    top="50%"
                                    left="8%"
                                    zIndex={1}
                                    transform={'translateY(-50%)'}
                                >
                                    <Heading as="h2" size="xl">
                                        {heading}
                                    </Heading>
                                    <Text>{subHeading}</Text>
                                    {buttonText && (
                                        <Button
                                            href={buttonUrl}
                                            onClick={(e) => {
                                                // segment
                                                segment.sendPanelSelectedEvent(e, {})
                                            }}
                                            size="lg"
                                            bg="rgba(255, 255, 255, 0.1)"
                                            mt={4}
                                            border="1px"
                                            borderColor={textColor}
                                        >
                                            {buttonText}
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        )
                    ) : (
                        <></>
                    )}
                </Box>
            </HideOnDesktop>
        </>
    )
}

VideoWithTextBanner.propTypes = {
    videoUrl: PropTypes.string,
    custom: PropTypes.object,
    imageAltText: PropTypes.string,
    imageLink: PropTypes.string,
    backgroundColor: PropTypes.string,
    textColor: PropTypes.string,
    heading: PropTypes.string,
    subHeading: PropTypes.string,
    buttonText: PropTypes.string,
    buttonUrl: PropTypes.string
}

export default VideoWithTextBanner
